import { roomTaxesFromDiscounted } from './roomPricing';
import { resolveRoomDisplayImages } from './propertyGalleryImages';
import { isComingSoonDiscountedPrice } from './stayComingSoon';
import {
  guestPricingEnabled,
  minDiscountedForGuestPricing,
  normalizeGuestPricingForSave,
  resolveGuestOccupancySlice,
  getGuestConfigForWeekday,
  resolveNightPriceForOccupancy
} from './roomGuestPricing';

/** 0 = Sunday … 6 = Saturday; keys match `Date.UTC(y,m-1,d).getUTCDay()` for YYYY-MM-DD. */
export const ROOM_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const ROOM_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseYmdUtc(ymd) {
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

function formatYmdUtc(dt) {
  const y = dt.getUTCFullYear();
  const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function addOneDayYmd(ymd) {
  const dt = parseYmdUtc(ymd);
  if (!dt) return ymd;
  dt.setUTCDate(dt.getUTCDate() + 1);
  return formatYmdUtc(dt);
}

function utcWeekdayFromYmd(ymd) {
  const dt = parseYmdUtc(ymd);
  if (!dt || Number.isNaN(dt.getTime())) return 0;
  return dt.getUTCDay();
}

function normalizeSlice({ mrp, discountedPrice, currency }) {
  const cur = currency || 'INR';
  const dp = Number(discountedPrice) || 0;
  const m = Number(mrp) || 0;
  const tx = roomTaxesFromDiscounted(dp);
  return {
    mrp: m,
    discountedPrice: dp,
    taxes: tx,
    currency: cur
  };
}

function baseSliceFromPrice(price) {
  const p = price && typeof price === 'object' ? price : {};
  return normalizeSlice({
    mrp: p.mrp,
    discountedPrice: p.discountedPrice,
    taxes: p.taxes,
    currency: p.currency
  });
}

export function sliceForUtcWeekdayFromPrice(price, weekday0to6) {
  const p = price && typeof price === 'object' ? price : {};
  const use = Boolean(p.useDayOfWeekPricing);
  const key = ROOM_DAY_KEYS[weekday0to6] || 'sun';
  const day = use && p.dayPricing && typeof p.dayPricing === 'object' ? p.dayPricing[key] : null;
  if (day && day.discountedPrice != null) {
    return normalizeSlice({
      mrp: day.mrp != null ? day.mrp : p.mrp,
      discountedPrice: day.discountedPrice,
      taxes: day.taxes,
      currency: p.currency
    });
  }
  return baseSliceFromPrice(p);
}

export function sliceForNightFromPrice(price, dateYmd) {
  return sliceForUtcWeekdayFromPrice(price, utcWeekdayFromYmd(dateYmd));
}

export function minDiscountedAcrossWeek(roomOrPrice) {
  const p =
    roomOrPrice && roomOrPrice.price && typeof roomOrPrice.price === 'object'
      ? roomOrPrice.price
      : roomOrPrice;
  if (!p || typeof p !== 'object') return 0;
  const cap =
    roomOrPrice && roomOrPrice.capacity != null ? roomOrPrice.capacity : undefined;
  const guestMin = minDiscountedForGuestPricing(p, cap);
  if (guestMin != null) return guestMin;
  if (!p.useDayOfWeekPricing || !p.dayPricing || typeof p.dayPricing !== 'object') {
    return baseSliceFromPrice(p).discountedPrice;
  }
  let min = Infinity;
  for (let w = 0; w < 7; w += 1) {
    min = Math.min(min, sliceForUtcWeekdayFromPrice(p, w).discountedPrice);
  }
  return min === Infinity ? 0 : min;
}

export function defaultDayPricingFromBase({ mrp, discountedPrice }) {
  const dp = Number(discountedPrice) || 0;
  const m = Number(mrp) || 0;
  const slice = {
    mrp: m,
    discountedPrice: dp,
    taxes: roomTaxesFromDiscounted(dp)
  };
  const out = {};
  for (const k of ROOM_DAY_KEYS) {
    out[k] = { ...slice };
  }
  return out;
}

/**
 * Lowest nightly sell rate within a stay window (catalog; ignores inventory overrides).
 * @returns {{ discountedPrice: number, mrp: number } | null}
 */
export function minDiscountedAcrossStay(roomOrPrice, checkInYmd, checkOutYmd, adults = null) {
  const p =
    roomOrPrice && roomOrPrice.price && typeof roomOrPrice.price === 'object'
      ? roomOrPrice.price
      : roomOrPrice;
  if (!p || typeof p !== 'object') return null;

  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomOrPrice?.capacity) || 10)));
  const start = String(checkInYmd).slice(0, 10);
  const end = String(checkOutYmd).slice(0, 10);
  if (!start || !end || start >= end) return null;

  const adultCount =
    adults != null
      ? Math.max(1, Math.floor(Number(adults) || 1))
      : guestPricingEnabled(p)
        ? 1
        : null;

  let minDp = Infinity;
  let minMrp = 0;
  for (let cur = start; cur < end; cur = addOneDayYmd(cur)) {
    const eff =
      adultCount != null && guestPricingEnabled(p)
        ? resolveNightPriceForOccupancy(p, cur, adultCount, cap)
        : sliceForNightFromPrice(p, cur);
    if (eff.discountedPrice < minDp) {
      minDp = eff.discountedPrice;
      minMrp = eff.mrp;
    }
  }
  if (minDp === Infinity) return null;
  return { discountedPrice: minDp, mrp: minMrp };
}

/** Cheapest room-night in a stay across all rooms (for “Starting from”). */
export function minStayPriceAcrossRooms(rooms, checkInYmd, checkOutYmd, adults = null) {
  if (!Array.isArray(rooms) || !rooms.length) return null;
  let best = null;
  for (const room of rooms) {
    const slice = minDiscountedAcrossStay(room, checkInYmd, checkOutYmd, adults);
    if (!slice) continue;
    if (!best || slice.discountedPrice < best.discountedPrice) {
      best = slice;
    }
  }
  return best;
}

/** Catalog-only estimate (ignores per-date inventory price overrides). */
export function sumCatalogNights(price, checkInYmd, checkOutYmd, adults = null, roomCapacity = 10) {
  const start = String(checkInYmd).slice(0, 10);
  const end = String(checkOutYmd).slice(0, 10);
  let nights = 0;
  let sub = 0;
  let tax = 0;
  const adultCount =
    adults != null ? Math.max(1, Math.floor(Number(adults) || 1)) : null;
  const cap = Math.max(1, Math.min(20, Math.floor(Number(roomCapacity) || 10)));
  for (let cur = start; cur < end; cur = addOneDayYmd(cur)) {
    nights += 1;
    const eff =
      adultCount != null && guestPricingEnabled(price)
        ? resolveNightPriceForOccupancy(price, cur, adultCount, cap)
        : sliceForNightFromPrice(price, cur);
    sub += eff.discountedPrice;
    tax += eff.taxes;
  }
  return { nights, subTotal: sub, totalTaxes: tax, grandTotal: sub };
}

export function dateToYmd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Maps API room → RoomSelection card fields (`price` / `taxes` are per-night averages when dates set). */
export function mapStayRoomForSelectionUI(room, selectedDatesState, quoteAdults = null, stay = null) {
  const raw = room.price;
  const adults =
    quoteAdults != null
      ? Math.max(1, Math.floor(Number(quoteAdults) || 1))
      : null;
  const yIn =
    selectedDatesState?.checkInDate instanceof Date
      ? dateToYmd(selectedDatesState.checkInDate)
      : null;
  const yOut =
    selectedDatesState?.checkOutDate instanceof Date
      ? dateToYmd(selectedDatesState.checkOutDate)
      : null;

  let price = 0;
  let taxes = 0;
  let originalPrice = Number(raw?.mrp) || 0;

  const cap = room.capacity || 10;

  if (yIn && yOut && yIn < yOut) {
    const sum = sumCatalogNights(raw, yIn, yOut, adults, cap);
    const n = sum.nights || 1;
    price = Math.round((sum.subTotal / n) * 100) / 100;
    taxes = Math.round((sum.totalTaxes / n) * 100) / 100;
  } else if (adults != null && guestPricingEnabled(raw)) {
    const cfg = getGuestConfigForWeekday(raw, 'sun', cap);
    const slice = cfg
      ? resolveGuestOccupancySlice(cfg, adults)
      : baseSliceFromPrice(raw);
    price = slice.discountedPrice;
    taxes = slice.taxes;
    originalPrice = slice.mrp || originalPrice;
  } else {
    price = minDiscountedAcrossWeek({ price: raw });
    taxes = roomTaxesFromDiscounted(price);
    if (!originalPrice) originalPrice = price;
  }

  const displayImages = resolveRoomDisplayImages(room, stay);

  return {
    id: room.roomId,
    comingSoon: isComingSoonDiscountedPrice(minDiscountedAcrossWeek(room)),
    capacity: room.capacity,
    name: room.title,
    description: room.description || '',
    image: displayImages[0],
    bedType:
      room.features?.find((f) => f.title && f.title.includes('Bed'))?.title ||
      'Bed configuration not specified',
    sleeps: room.capacity || 'Capacity not specified',
    price,
    originalPrice,
    taxes,
    images: displayImages,
    options: room.options,
    amenities: room.amenities || [],
    features: room.features || []
  };
}

/** Normalize taxes on base + each day slice before API submit. */
export function normalizeRoomPriceForSave(price, roomCapacity = 10) {
  let p = normalizeGuestPricingForSave({ ...(price || {}) }, roomCapacity);
  const baseDp = Number(p.discountedPrice) || 0;
  p.taxes = roomTaxesFromDiscounted(baseDp);
  if (p.useDayOfWeekPricing && p.dayPricing && typeof p.dayPricing === 'object') {
    const next = { ...p.dayPricing };
    for (const k of ROOM_DAY_KEYS) {
      if (next[k] && next[k].discountedPrice != null) {
        const d = Number(next[k].discountedPrice) || 0;
        next[k] = {
          ...next[k],
          taxes: roomTaxesFromDiscounted(d)
        };
      }
    }
    p.dayPricing = next;
  } else {
    p.useDayOfWeekPricing = false;
    p.dayPricing = {};
  }
  return p;
}
